import { useEffect, useRef } from 'react';

import { updateSnowplowIdentity } from '../snowplow';
import type { ContextCustomer } from '../snowplow';
import { useAppSelector } from '../../redux/app/hooks';
import { RootState } from '../../redux/app/store';

/**
 * Syncs Snowplow identity (userId + customer context) when host context and/or
 * active user become available. Sends only non-PII: customer_id (Visma CustomerID),
 * setUserId (User/EmployeeID), and optional CustomerType for Data Lake join.
 * Customer context is added only once to avoid duplicate global contexts.
 */
export function useSnowplowIdentity(): void {
    const hostContext = useAppSelector((state: RootState) => state.hostContext.hostContext);
    const hostContextInitialized = useAppSelector((state: RootState) => state.hostContext.isInitialized);
    const activeUserInfo = useAppSelector((state: RootState) => state.app.activeUserInfo);
    const hasAddedCustomerContextRef = useRef(false);

    useEffect(() => {
        if (!hostContextInitialized) return;

        const userId = activeUserInfo?.id;
        const customerId = hostContext.vismaCustomerId;
        const customerContext: ContextCustomer | undefined =
            customerId || hostContext.companyType
                ? {
                      ...(customerId && { customer_id: customerId }),
                      ...(hostContext.companyType && { customfield_customer1: hostContext.companyType }),
                  }
                : undefined;

        const shouldAddCustomer = customerContext && !hasAddedCustomerContextRef.current;
        if (shouldAddCustomer) {
            hasAddedCustomerContextRef.current = true;
        }

        updateSnowplowIdentity(userId, shouldAddCustomer ? { customer: customerContext } : undefined);
    }, [hostContextInitialized, hostContext.vismaCustomerId, hostContext.companyType, activeUserInfo?.id]);
}
